import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { NoteEvent, PitchContourPoint, Frame } from '../types';
import { slopeColor, noteIntensity, NOTE_NAMES } from '../utils';

export function PianoRoll({
  notes, duration, currentTime, selectedNote, onSelectNote, pitchContour, frames,
  loopRegion, onLoopChange
}: {
  notes: NoteEvent[];
  duration: number;
  currentTime: number;
  selectedNote: number | null;
  onSelectNote: (i: number | null, clickTime?: number) => void;
  pitchContour?: PitchContourPoint[];
  frames?: Frame[];
  loopRegion: { start: number; end: number } | null;
  onLoopChange: (loop: { start: number; end: number } | null | ((prev: { start: number; end: number } | null) => { start: number; end: number } | null)) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredNote, setHoveredNote] = useState<number | null>(null);

  // Zoom: viewStart/viewEnd in seconds
  const [viewStart, setViewStart] = useState(0);
  const [viewEnd, setViewEnd] = useState(duration);

  // Vertical pitch range (scrollable) — includes both notes AND pitch contour
  const dataMidiRange = useMemo(() => {
    let lo = Infinity, hi = -Infinity;
    for (const n of notes) {
      if (n.midi_num < lo) lo = n.midi_num;
      if (n.midi_num > hi) hi = n.midi_num;
    }
    // Include pitch contour so contour is always visible in the grid
    if (pitchContour) {
      for (const pt of pitchContour) {
        if (pt.f0 > 0) {
          const midi = 69 + 12 * Math.log2(pt.f0 / 440);
          if (midi < lo) lo = midi;
          if (midi > hi) hi = midi;
        }
      }
    }
    if (!isFinite(lo)) return { min: 48, max: 72 };
    return {
      min: Math.floor(lo) - 2,
      max: Math.ceil(hi) + 2,
    };
  }, [notes, pitchContour]);

  const [midiViewMin, setMidiViewMin] = useState(dataMidiRange.min);
  const [midiViewMax, setMidiViewMax] = useState(dataMidiRange.max);

  // Effective duration: covers the full audio AND all note/contour data
  const dataEnd = notes.length > 0 ? Math.max(...notes.map(n => n.end_s)) : 0;
  const contourEnd = pitchContour && pitchContour.length > 0
    ? pitchContour[pitchContour.length - 1].t : 0;
  const effectiveDuration = Math.max(duration, dataEnd, contourEnd);

  // Ensure viewEnd covers the full effective duration
  const effectiveViewEnd = Math.max(viewEnd, effectiveDuration);
  const viewDur = effectiveViewEnd - viewStart;

  const midiRange = { min: midiViewMin, max: midiViewMax };

  // Reset view when data changes
  useEffect(() => { setViewStart(0); setViewEnd(effectiveDuration); }, [effectiveDuration]);
  useEffect(() => {
    setMidiViewMin(dataMidiRange.min);
    setMidiViewMax(dataMidiRange.max);
  }, [dataMidiRange]);

  // Intensity range for color normalization
  const dbRange = useMemo(() => {
    if (!frames) return { min: -50, max: -10 };
    const dbs = frames.map(f => f.intensity_db).filter(v => v != null);
    if (dbs.length < 2) return { min: -50, max: -10 };
    dbs.sort((a, b) => a - b);
    return {
      min: dbs[Math.floor(dbs.length * 0.05)],
      max: dbs[Math.floor(dbs.length * 0.95)],
    };
  }, [frames]);


  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    const W = rect.width;
    const H = rect.height;
    const labelW = 40;
    const plotW = W - labelW;
    const range = midiRange.max - midiRange.min;
    const gridTop = 12; // reserved for zoom + loop bars (set later in draw)
    const gridH = H - gridTop;
    const noteH = gridH / range;

    // Helpers
    const tToX = (t: number) => labelW + ((t - viewStart) / viewDur) * plotW;
    const midiToY = (midi: number) => gridTop + gridH - ((midi - midiRange.min) / range) * gridH;

    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Grid
    for (let midi = Math.floor(midiRange.min); midi <= Math.ceil(midiRange.max); midi++) {
      const y = midiToY(midi);
      const noteIdx = midi % 12;
      const isBlack = [1, 3, 6, 8, 10].includes(noteIdx);
      ctx.strokeStyle = isBlack ? '#1a1a2e' : '#222240';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(labelW, y);
      ctx.lineTo(W, y);
      ctx.stroke();
      if (!isBlack) {
        const octave = Math.floor(midi / 12) - 1;
        ctx.fillStyle = '#666';
        ctx.font = '10px monospace';
        ctx.textAlign = 'right';
        ctx.fillText(`${NOTE_NAMES[noteIdx]}${octave}`, labelW - 4, y + 3);
      }
    }

    // Time markers
    const timeStep = viewDur > 20 ? 5 : viewDur > 10 ? 2 : viewDur > 4 ? 1 : 0.5;
    ctx.fillStyle = '#444';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (let ts = Math.ceil(viewStart / timeStep) * timeStep; ts <= effectiveViewEnd; ts += timeStep) {
      const x = tToX(ts);
      if (x > labelW && x < W) {
        ctx.fillText(`${ts.toFixed(ts % 1 ? 1 : 0)}s`, x, H - 3);
      }
    }

    // Clip to plot area
    ctx.save();
    ctx.beginPath();
    ctx.rect(labelW, 0, plotW, H);
    ctx.clip();

    // Note blocks — color shifts per-frame along time axis
    notes.forEach((note, i) => {
      const x = tToX(note.start_s);
      const xEnd = tToX(note.end_s);
      const w = Math.max(3, xEnd - x);
      if (x + w < labelW || x > W) return;
      const y = midiToY(note.midi_num) - noteH / 2;
      const h = Math.max(noteH * 0.8, 6);
      const isSelected = selectedNote === i;
      const isHovered = hoveredNote === i;
      ctx.globalAlpha = isSelected ? 1.0 : isHovered ? 0.9 : 0.75;

      // Get per-frame H1-H5 values for this note
      const frameVals: { t: number; h1h5: number }[] = [];
      if (frames && note.frame_indices) {
        for (const fi of note.frame_indices) {
          const f = frames[fi];
          if (!f) continue;
          const hh = f.harmonics_db;
          const db = f.intensity_db;
          if (db != null) {
            frameVals.push({ t: f.time_s, h1h5: db });
          }
        }
      }

      if (frameVals.length >= 2 && w > 6) {
        // Smooth gradient using canvas linearGradient with per-frame color stops
        const grad = ctx.createLinearGradient(x, 0, x + w, 0);
        for (const fv of frameVals) {
          const stop = Math.max(0, Math.min(1, (tToX(fv.t) - x) / w));
          grad.addColorStop(stop, slopeColor(fv.h1h5, dbRange.min, dbRange.max));
        }
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 3);
        ctx.fill();
      } else {
        // Fallback: single color
        const h1h5 = noteIntensity(note, frames);
        ctx.fillStyle = slopeColor(h1h5, dbRange.min, dbRange.max);
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 3);
        ctx.fill();
      }

      if (isSelected) {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 3);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (isHovered) {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 3);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      if (w > 30) {
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(note.note, x + 4, y + h / 2 + 3);
      }
    });

    ctx.globalAlpha = 1;

    // Pitch contour — downsample to ~1pt/pixel, draw with quadratic curves
    if (pitchContour && pitchContour.length > 0) {
      ctx.save();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      // Downsample: average points that fall within the same pixel
      const pixelDt = viewDur / plotW; // seconds per pixel
      const segments: { x: number; y: number }[][] = [];
      let curSeg: { x: number; y: number }[] = [];
      let bucketT = -1, bucketMidiSum = 0, bucketCount = 0, lastT = -1;

      const flushBucket = () => {
        if (bucketCount > 0) {
          const avgMidi = bucketMidiSum / bucketCount;
          curSeg.push({ x: tToX(bucketT), y: midiToY(avgMidi) });
        }
      };

      for (const pt of pitchContour) {
        if (pt.f0 <= 0 || pt.t < viewStart - 0.1 || pt.t > effectiveViewEnd + 0.1) {
          flushBucket();
          if (curSeg.length >= 2) segments.push(curSeg);
          curSeg = [];
          bucketCount = 0;
          lastT = -1;
          continue;
        }
        const midi = 69 + 12 * Math.log2(pt.f0 / 440);
        // Break on time gap or pitch jump
        if (lastT >= 0 && ((pt.t - lastT) > 0.015 || (bucketCount > 0 && Math.abs(midi - bucketMidiSum / bucketCount) > 3))) {
          flushBucket();
          if (curSeg.length >= 2) segments.push(curSeg);
          curSeg = [];
          bucketCount = 0;
        }
        // Accumulate into pixel bucket
        if (bucketCount === 0 || (pt.t - bucketT) >= pixelDt) {
          flushBucket();
          bucketT = pt.t; bucketMidiSum = midi; bucketCount = 1;
        } else {
          bucketMidiSum += midi; bucketCount++;
        }
        lastT = pt.t;
      }
      flushBucket();
      if (curSeg.length >= 2) segments.push(curSeg);

      // Draw segments with quadratic curves
      const drawSmooth = () => {
        ctx.beginPath();
        for (const seg of segments) {
          if (seg.length < 2) continue;
          ctx.moveTo(seg[0].x, seg[0].y);
          if (seg.length === 2) {
            ctx.lineTo(seg[1].x, seg[1].y);
          } else {
            // Quadratic curves through midpoints for smooth interpolation
            for (let i = 0; i < seg.length - 1; i++) {
              const cx = (seg[i].x + seg[i + 1].x) / 2;
              const cy = (seg[i].y + seg[i + 1].y) / 2;
              ctx.quadraticCurveTo(seg[i].x, seg[i].y, cx, cy);
            }
            // Last point
            const last = seg[seg.length - 1];
            ctx.lineTo(last.x, last.y);
          }
        }
      };

      // Dark outline
      drawSmooth();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3.5;
      ctx.globalAlpha = 0.4;
      ctx.stroke();

      // Bright contour
      drawSmooth();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.8;
      ctx.globalAlpha = 0.9;
      ctx.stroke();

      ctx.restore();
      ctx.globalAlpha = 1;
    }

    ctx.restore(); // un-clip

    // Cursor
    const cursorX = tToX(currentTime);
    if (cursorX >= labelW && cursorX <= W) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cursorX, gridTop);
      ctx.lineTo(cursorX, H);
      ctx.stroke();
    }

    // Top bars (above the note grid)
    // Layout: zoom bar (3px) → loop bar (8px) → note grid
    const zoomBarH = 3;
    const loopBarH = 8;
    const topMargin = zoomBarH + loopBarH + 1;

    // Zoom indicator (row 1, top)
    ctx.fillStyle = '#222';
    ctx.fillRect(labelW, 0, plotW, zoomBarH);
    if (viewDur < effectiveDuration - 0.1) {
      const barStart = labelW + (viewStart / effectiveDuration) * plotW;
      const barW = (viewDur / effectiveDuration) * plotW;
      ctx.fillStyle = '#60a5fa';
      ctx.fillRect(barStart, 0, Math.max(barW, 4), zoomBarH);
    }

    // Loop bar (row 2, below zoom)
    const loopY = zoomBarH;
    if (loopRegion) {
      const lx = tToX(loopRegion.start);
      const rx = tToX(loopRegion.end);

      // Shaded region on note grid
      ctx.fillStyle = 'rgba(250, 204, 21, 0.06)';
      ctx.fillRect(lx, topMargin, rx - lx, H - topMargin);

      // Loop bar
      ctx.fillStyle = 'rgba(250, 204, 21, 0.6)';
      ctx.fillRect(lx, loopY, rx - lx, loopBarH);

      // Left brace: ] shape pointing right
      const braceH = loopBarH + 8;
      ctx.fillStyle = '#facc15';
      ctx.beginPath();
      ctx.moveTo(lx, loopY);
      ctx.lineTo(lx + 8, loopY + braceH / 2);
      ctx.lineTo(lx, loopY + braceH);
      ctx.closePath();
      ctx.fill();

      // Right brace: [ shape pointing left
      ctx.beginPath();
      ctx.moveTo(rx, loopY);
      ctx.lineTo(rx - 8, loopY + braceH / 2);
      ctx.lineTo(rx, loopY + braceH);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = 'rgba(60, 60, 60, 0.3)';
      ctx.fillRect(labelW, loopY, plotW, loopBarH);
    }
  }, [notes, duration, effectiveDuration, currentTime, selectedNote, hoveredNote, midiRange, NOTE_NAMES, pitchContour, viewStart, viewEnd, viewDur, loopRegion]);

  useEffect(() => { draw(); }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  // Scroll: on label area = scroll pitch, on plot area = zoom time
  // Click: check if on a note block. If not, start drag (pan/zoom).
  const dragRef = useRef({ active: false, didMove: false, startX: 0, startY: 0, viewStart: 0, viewEnd: 0, type: 'pan' as 'pan' | 'pitch', midiMin: 0, midiRange: 0 });
  const loopDragRef = useRef<{ type: 'create' | 'move-start' | 'move-end' | 'move-loop'; startTime: number } | null>(null);

  const hitTestNote = useCallback((e: React.MouseEvent): number => {
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const labelW = 40;
    const plotW = rect.width - labelW;
    const clickTime = viewStart + ((x - labelW) / plotW) * viewDur;
    const range = midiRange.max - midiRange.min;
    const gridTop = 12;
    const gridH = rect.height - gridTop;
    const clickMidi = midiRange.min + (1 - (y - gridTop) / gridH) * range;

    for (let i = 0; i < notes.length; i++) {
      const n = notes[i];
      if (clickTime >= n.start_s && clickTime <= n.end_s
          && Math.abs(clickMidi - n.midi_num) < 2) {
        return i;
      }
    }
    return -1;
  }, [notes, viewStart, viewDur, midiRange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const my = e.clientY - rect.top;
    const mx = e.clientX - rect.left;
    const labelW = 40;
    const plotW = rect.width - labelW;
    const clickTime = viewStart + ((mx - labelW) / plotW) * viewDur;

    // Check loop bar area (top 12px)
    if (my < 12) {
      e.preventDefault();
      if (loopRegion) {
        const tToXLocal = (t: number) => labelW + ((t - viewStart) / viewDur) * plotW;
        const lx = tToXLocal(loopRegion.start);
        const rx = tToXLocal(loopRegion.end);
        // Near left edge — store pixel offset from edge
        if (mx >= lx - 4 && mx <= lx + 10) {
          loopDragRef.current = { type: 'move-start', startTime: mx - lx };
          return;
        }
        // Near right edge — store pixel offset from edge
        if (mx >= rx - 10 && mx <= rx + 4) {
          loopDragRef.current = { type: 'move-end', startTime: mx - rx };
          return;
        }
        // Inside loop body — store pixel offset from left edge
        if (clickTime >= loopRegion.start && clickTime <= loopRegion.end) {
          loopDragRef.current = { type: 'move-loop', startTime: mx - lx };
          return;
        }
      }
      // Outside any loop — create new
      loopDragRef.current = { type: 'create', startTime: clickTime };
      onLoopChange({ start: clickTime, end: clickTime });
      return;
    }

    // Double-click loop bar to clear
    // (handled by handleDblClick)

    // Label area: drag to pan pitch
    if (mx < labelW) {
      e.preventDefault();
      dragRef.current = {
        active: true, didMove: false,
        startX: e.clientX, startY: e.clientY,
        viewStart, viewEnd, type: 'pitch', midiMin: midiViewMin, midiRange: midiViewMax - midiViewMin,
      };
      return;
    }

    dragRef.current = {
      active: true, didMove: false,
      startX: e.clientX, startY: e.clientY,
      viewStart, viewEnd, type: 'pan', midiMin: midiViewMin, midiRange: midiViewMax - midiViewMin,
    };
  }, [viewStart, viewEnd, hitTestNote]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Note hover detection when not dragging
    if (!loopDragRef.current && !dragRef.current.active) {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const labelW = 40;
        const plotW = rect.width - labelW;
        const gridTop = 12;
        const gridH = rect.height - gridTop;
        const range = midiRange.max - midiRange.min;
        const hoverTime = viewStart + ((mx - labelW) / plotW) * viewDur;
        const hoverMidi = midiRange.min + (1 - (my - gridTop) / gridH) * range;

        let found: number | null = null;
        for (let i = 0; i < notes.length; i++) {
          const n = notes[i];
          if (hoverTime >= n.start_s && hoverTime <= n.end_s
              && Math.abs(hoverMidi - n.midi_num) < 2) {
            found = i;
            break;
          }
        }
        setHoveredNote(found);
      }
    }

    // Loop drag
    if (loopDragRef.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const labelW = 40;
      const plotW = rect.width - labelW;
      const t = viewStart + ((mx - labelW) / plotW) * viewDur;
      const clamped = Math.max(0, Math.min(duration, t));

      const d = loopDragRef.current;
      // Convert pixel position to time, accounting for stored offset
      const pxToTime = (px: number) => {
        const time = viewStart + ((px - labelW) / plotW) * viewDur;
        return Math.max(0, Math.min(duration, time));
      };

      if (d.type === 'create') {
        onLoopChange({ start: Math.min(d.startTime, clamped), end: Math.max(d.startTime, clamped) });
      } else if (d.type === 'move-start') {
        const edgeX = mx - d.startTime; // subtract pixel offset from edge
        const edgeTime = pxToTime(edgeX);
        onLoopChange(prev => prev ? { start: Math.min(edgeTime, prev.end - 0.1), end: prev.end } : prev);
      } else if (d.type === 'move-end') {
        const edgeX = mx - d.startTime;
        const edgeTime = pxToTime(edgeX);
        onLoopChange(prev => prev ? { start: prev.start, end: Math.max(edgeTime, prev.start + 0.1) } : prev);
      } else if (d.type === 'move-loop') {
        const loopLeftX = mx - d.startTime;
        const newStart = pxToTime(loopLeftX);
        onLoopChange(prev => {
          if (!prev) return prev;
          const dur = prev.end - prev.start;
          const ns = Math.max(0, Math.min(duration - dur, newStart));
          return { start: ns, end: ns + dur };
        });
      }
      return;
    }

    const d = dragRef.current;
    if (!d.active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const labelW = 40;
    const plotW = rect.width - labelW;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.didMove && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;
    d.didMove = true;

    // Pitch label area: Y = pan, X = zoom range
    if (d.type === 'pitch') {
      const origRange = d.midiRange;

      // Y drag: pan pitch up/down
      const midiShift = (dy / rect.height) * origRange;
      const pannedMin = d.midiMin + midiShift;

      // X drag: zoom pitch range (drag right = fewer notes, drag left = more)
      const zoomFactor = Math.pow(1.008, -dx);
      const newRange = Math.max(6, Math.min(80, origRange * zoomFactor));
      const mid = pannedMin + origRange / 2;
      const newMin = Math.max(20, mid - newRange / 2);

      setMidiViewMin(newMin);
      setMidiViewMax(newMin + newRange);
      return;
    }

    const origDur = d.viewEnd - d.viewStart;

    // Step 1: Pan — horizontal drag shifts the view
    const dt = -(dx / plotW) * origDur;
    let newStart = d.viewStart + dt;
    let newEnd = d.viewEnd + dt;

    // Step 2: Zoom — vertical drag scales duration around the CURRENT mouse position
    const mouseFrac = Math.max(0, Math.min(1, (e.clientX - rect.left - labelW) / plotW));
    const mouseT = newStart + mouseFrac * origDur;
    const zoomFactor = Math.pow(1.005, -dy);
    const newDur = Math.min(effectiveDuration, Math.max(0.5, origDur * zoomFactor));
    newStart = mouseT - mouseFrac * newDur;
    newEnd = newStart + newDur;


    // Clamp
    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd > effectiveDuration) { newStart -= (newEnd - effectiveDuration); newEnd = effectiveDuration; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(effectiveDuration, newEnd);

    setViewStart(newStart);
    setViewEnd(newEnd);
  }, [effectiveDuration, notes, midiRange, viewStart, viewDur]);


  const loopInteracted = useRef(false);

  const handleMouseUp = useCallback(() => {
    if (loopDragRef.current) {
      loopInteracted.current = true;
      // If loop is too small (< 0.2s), clear it
      if (loopRegion && loopRegion.end - loopRegion.start < 0.2) {
        onLoopChange(null);
      }
      loopDragRef.current = null;
      return;
    }
    dragRef.current.active = false;
  }, [loopRegion, onLoopChange]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (dragRef.current.didMove) {
      dragRef.current.didMove = false;
      return;
    }
    if (loopInteracted.current) {
      loopInteracted.current = false;
      return;
    }
    // Get click time from position
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const labelW = 40;
    const plotW = rect.width - labelW;
    const clickTime = viewStart + ((x - labelW) / plotW) * viewDur;

    const noteIdx = hitTestNote(e);
    onSelectNote(
      noteIdx >= 0 ? (selectedNote === noteIdx ? null : noteIdx) : null,
      clickTime
    );
  }, [hitTestNote, selectedNote, onSelectNote, viewStart, viewDur]);

  // Double-click to reset zoom
  const handleDblClick = useCallback((e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const my = e.clientY - rect.top;
      // Double-click on loop bar clears loop
      if (my < 12 && loopRegion) {
        onLoopChange(null);
        return;
      }
    }
    setViewStart(0);
    setViewEnd(effectiveDuration);
  }, [effectiveDuration, loopRegion, onLoopChange]);

  return (
    <div className="piano-roll-container">
      <canvas
        ref={canvasRef}
        className="piano-roll-canvas"
        onClick={handleClick}
        onDoubleClick={handleDblClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); setHoveredNote(null); }}
      />
    </div>
  );
}
